<template>
  <div class="workReports">
    <div class="buttons">
      <b-button id="addNewRowButton" @click="addEntry">
        <i class="bi bi-plus-square"></i>
      </b-button>

      <div id="summaryDescriptionBlock">
        <div class="summaryDescription">{{ $t(localeConsts.workReportsTotalHours) }} {{ workingHoursTotal }}</div>
        <p class="summaryDescription">|</p>
        <div class="summaryDescription">{{ $t(localeConsts.workReportsTotalWeekHours) }} {{ workHoursCurrentWeek }}</div>
      </div>

      <div id="monthFilter">
        <div id="weekFilterText">{{ $t(localeConsts.filterByWeek) }}</div>

        <b-input
          id="weekFilterButton"
          v-model="week"
          type="number"
          @input="filterOnWeek(parseInt(week), { data: tableData, dateColumn: 'task_start' })"
        ></b-input>
      </div>
    </div>
    <ValidationObserver v-slot="{ invalid }">
      <div class="dataTable">
        <b-table class="workReportsTable w-auto" :items="tableData" :fields="columns" striped sticky-header>
          <template #cell(weekNumber)="data">
            <span>{{ data.value }}</span>
          </template>

          <template #cell(task_start)="data">
            <ValidationProvider v-if="tableData[data.index].isEdit" v-slot="{ errors }" name="date" rules="required">
              <div v-if="tableData[data.index].isEdit" class="inputCenterAlignment">
                <b-form-datepicker
                  v-model="tableData[data.index].task_start"
                  size="sm"
                  class="calendar w-100"
                  button-only
                  :max="maxDate"
                  :min="minDate"
                  value-as-date
                ></b-form-datepicker>
              </div>
              <span class="studentTableErrorMessages">{{ errors[0] }}</span>
            </ValidationProvider>
            <span v-else>{{ formatWorkReportDate(data.value) }}</span>
          </template>

          <template #cell(task_duration)="data">
            <ValidationProvider
              v-if="tableData[data.index].isEdit"
              v-slot="{ errors }"
              name="hours"
              rules="required|between:0,12|half_step"
            >
              <div v-if="tableData[data.index].isEdit" class="inputCenterAlignment">
                <b-form-input id="numberInput" v-model="tableData[data.index].task_duration" type="number" step="0.5"></b-form-input>
              </div>
              <span class="studentTableErrorMessages">{{ errors[0] }}</span>
            </ValidationProvider>
            <span v-else>{{ data.value }}</span>
          </template>

          <template #cell(task)="data">
            <ValidationProvider v-if="tableData[data.index].isEdit" v-slot="{ errors }" name="description" rules="required|max:256">
              <b-form-textarea
                v-if="tableData[data.index].isEdit"
                v-model="tableData[data.index].task"
                :placeholder="columns[3].placeholder"
                rows="5"
                cols="40"
                class="bigInputs"
              ></b-form-textarea>
              <span class="studentTableErrorMessages">{{ errors[0] }}</span>
            </ValidationProvider>
            <span v-else class="textAreaSpan">{{ data.value }}</span>
          </template>

          <template #cell(results)="data">
            <ValidationProvider v-if="tableData[data.index].isEdit" v-slot="{ errors }" name="results" rules="required|max:256">
              <b-form-textarea
                v-if="tableData[data.index].isEdit"
                v-model="tableData[data.index].results"
                type="text"
                :value="data.value"
                :placeholder="columns[4].placeholder"
                class="bigInputs"
              ></b-form-textarea>
              <span class="studentTableErrorMessages">{{ errors[0] }}</span>
            </ValidationProvider>
            <span v-else class="textAreaSpan">{{ data.value }}</span>
          </template>

          <template #cell(edit)="data">
            <b-button
              v-if="currentlyNotEditingOrDeletingEntry(data)"
              pill
              size="sm"
              :disabled="isDateNotOlderThanThisWeek(data)"
              @click="editRowHandler(data)"
            >
              <i class="bi bi-pencil-fill icon editButtons"></i>
            </b-button>
            <b-button v-if="!currentlyNotEditingOrDeletingEntry(data)" pill size="sm" :disabled="invalid" @click="confirmEditOrDelete(data)"
              ><i class="bi bi-check2 icon editButtons"></i
            ></b-button>

            <b-button
              v-if="currentlyNotEditingOrDeletingEntry(data)"
              id="deleteButton"
              pill
              size="sm"
              :disabled="invalid || isDateNotOlderThanThisWeek(data)"
              @click="promptToConfirmDelete(data)"
            >
              <i class="bi bi-trash-fill icon editButtons"></i>
            </b-button>
            <b-button v-if="!currentlyNotEditingOrDeletingEntry(data)" id="deleteButton" pill size="sm" @click="stopEditingOrDeleting(data)"
              ><i class="bi bi-x icon editButtons"></i
            ></b-button>
          </template>
        </b-table>
        <comment-data-table
          v-if="getProjectId"
          :comments-u-r-i="commentsURI"
          :comments-params="commentsParams"
          :week="parseInt(week)"
        ></comment-data-table>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import moment from 'moment'

import CommentDataTable from '../../../util/CommentComponent/CommentDataTable.vue'

import { workReportsMixin } from '../WorkReportsMixin'
import mapGettersMixin from '../../../mixins/mapGettersMixin'

import { generalHelperMethodsMixin } from '../../../mixins/generalHelperMethodsMixin'
import { editableTableMixin } from '../../../mixins/editableTableMixin'
import { filtersMixin } from '../../../mixins/filtersMixin'

import axios from '../../../../axios'

export default {
  name: 'WorkReportsStudent',
  components: {
    moment, //eslint-disable-line
    CommentDataTable //eslint-disable-line
  },
  mixins: [workReportsMixin, mapGettersMixin, generalHelperMethodsMixin, editableTableMixin, filtersMixin],
  data() {
    return {
      localeConsts: {
        workReportsDateColumn: 'WORKREPORTS.DATE',
        workReportsHoursColumn: 'WORKREPORTS.HOURS',
        workReportsDescriptionColumn: 'WORKREPORTS.DESCRIPTION',
        workReportsResultsColumn: 'WORKREPORTS.RESULTS',
        workReportsDescriptionPlaceholder: 'WORKREPORTS.DESCRIPTIONPLACEHOLDER',
        workReportsResultsPlaceholder: 'WORKREPORTS.RESULTSPLACEHOLDER',
        filterByWeek: 'WORKREPORTS.FILTER_BY_WEEK',
        workReportsTotalHours: 'WORKREPORTS.TOTAL_WORKINGHOURS',
        workReportsTotalWeekHours: 'WORKREPORTS.WORKINGHOURS_7DAYS'
      },
      maxDate: moment().endOf().format('YYYY-MM-DD hh:mm'),
      minDate: moment().subtract(7, 'days').startOf().format('YYYY-MM-DD hh:mm'),
      commentsURI: 'work-report-comments',
      week: this.$store.getters['authentication/getCurrentWeek'],
      fetchedData: [],
      tableData: []
    }
  },

  computed: {
    columns() {
      return [
        {
          label: 'PW',
          key: 'weekNumber',
          thClass: 'text-center studentTableThStyle',
          tdClass: 'text-center workReportsNumberCell'
        },
        {
          label: this.$i18n.t(this.localeConsts.workReportsDateColumn),
          key: 'task_start',
          thClass: 'text-center studentTableThStyle',
          tdClass: 'text-center workReportsNumberCell'
        },
        {
          label: this.$i18n.t(this.localeConsts.workReportsHoursColumn),
          key: 'task_duration',
          thClass: 'text-center studentTableThStyle',
          tdClass: 'text-center workReportsNumberCell'
        },
        {
          label: this.$i18n.t(this.localeConsts.workReportsDescriptionColumn),
          key: 'task',
          tdClass: 'tdTextArea text-center',
          thClass: 'text-center studentTableThStyle',
          placeholder: this.$i18n.t(this.localeConsts.workReportsDescriptionPlaceholder)
        },
        {
          label: this.$i18n.t(this.localeConsts.workReportsResultsColumn),
          key: 'results',
          tdClass: 'tdTextArea text-center ',
          thClass: 'text-center bg-white studentTableThStyle',
          placeholder: this.$i18n.t(this.localeConsts.workReportsResultsPlaceholder)
        },
        { key: 'edit', label: '', tdClass: 'text-center', thClass: 'text-center studentTableThStyle' }
      ]
    },

    currentWeek() {
      return this.getWeekNumber()
    },

    commentsParams() {
      return {
        params: {
          project_id: this.getProjectId
        }
      }
    },

    datePickerNoLabelBool() {
      return false
    },
    workHoursCurrentWeek() {
      return this.getWorkHoursPerWeek(this.fetchedData, this.currentWeek)
    },
    workingHoursTotal() {
      return this.getWorkHoursPerWeek(this.fetchedData)
    }
  },
  watch: {
    //eslint-disable-next-line
    currentWeek: function () {
      this.week = this.currentWeek
    }
  },

  mounted() {
    this.getReportsAndFilterByWeek()
  },

  methods: {
    addEntry() {
      // insert a dummy entry
      this.tableData.unshift({
        weekNumber: this.getWeekNumber(),
        task_start: moment().toDate(), //eslint-disable-line
        hours: '',
        task: '',
        results: '',
        isEdit: true,
        isDelete: false
      })
    },

    async confirmEditOrDelete(entry) {
      if (this.tableData[entry.index].isDelete) {
        this.deleteRow(entry.index)
      } else if (this.isNewEntry(entry)) {
        const responseProjectId = await this.getProjectId
        const taskStartMoment = moment(this.tableData[entry.index].task_start)
        const newEntry = {
          author: this.getUserId,
          project: responseProjectId,
          task_start: taskStartMoment.isSame(moment(), 'day') ? taskStartMoment.toISOString() : taskStartMoment.format('YYYY-MM-DD'),
          task_duration: this.tableData[entry.index].task_duration,
          task: this.tableData[entry.index].task,
          results: this.tableData[entry.index].results
        }

        axios.post('work-reports/', newEntry).then((response) => {
          this.tableData[entry.index].id = response.data.id
          this.fetchedData[entry.index] = newEntry
        })
      } else {
        const taskStartMoment = moment(this.tableData[entry.index].task_start)
        const patchEntry = {
          task_start: taskStartMoment.isSame(moment(), 'day') ? taskStartMoment.toISOString() : taskStartMoment.format('YYYY-MM-DD'),
          task_duration: this.tableData[entry.index].task_duration,
          task: this.tableData[entry.index].task,
          results: this.tableData[entry.index].results
        }
        const patchResponse = await axios.patch(`work-reports/${this.tableData[entry.index].id}/`, patchEntry)
        // is necessary in order to avoid bugs when canceling the editing
        const indexOfPatchedEntry = this.findFetchedDataIndexById(patchResponse.data.id)
        this.fetchedData[indexOfPatchedEntry] = patchEntry
      }

      this.stopEditingOrDeleting(entry, true)
    },

    deleteRow(index) {
      axios.delete(`work-reports/${this.tableData[index].id}/`)
      const indexOfDeletedEntry = this.findFetchedDataIndexById(this.tableData[index].id)
      this.tableData.splice(index, 1)
      this.fetchedData.splice(indexOfDeletedEntry, 1)
    },

    /*table helper methods */

    stopEditingOrDeleting(entry, isConfirm = false) {
      // if it is called by cancel then it should refresh the data by calling filterOnWeek
      this.tableData[entry.index].isDelete = false
      this.tableData[entry.index].isEdit = false

      isConfirm ? '' : this.filterOnWeek(this.week, { data: this.tableData, dateColumn: 'task_start' })
    }
  }
}
</script>

<style lang="scss">
// table styling

.workReportsTable {
  min-height: 33vh;
  max-height: 65vh;
}

.table .studentTableThStyle {
  background-color: #c1afd1 !important;
  color: #451a6e !important;
  max-width: 60px;
}

.inputCenterAlignment {
  display: grid;
  place-items: center;
}

// buttons above the header
#monthFilter {
  display: flex;
  align-items: center;
}

#weekFilterText {
  margin-right: 4em;
}

#weekFilterButton {
  width: 3em;
}
// summary description above the header

#summaryDescriptionBlock {
  display: flex;
  align-items: center;
}

.summaryDescription {
  color: #8b69a9;
  margin: 0px 7px;
}

// Table

.workReports {
  width: 100%;
}

.workReportsNumberCell {
  max-width: 110px;
  text-align: center;
}

// Inputs

.calendar .btn {
  background-color: #a981cc !important;
}

.bigInputs {
  height: 8em;
}

input::-webkit-inner-spin-button,
::-webkit-outer-spin-button {
  margin: 0px;
  -webkit-appearance: none;
}
#numberInput {
  width: 50px;
  text-align: center;
}

.buttons {
  display: flex;
  margin-bottom: 15px;
  justify-content: space-between;
  align-items: center;
}

//change, delete, confirm, cancel buttons

.editButtons {
  font-size: 16px;
  color: white;
}

#deleteButton {
  margin-left: 1rem;
}

//classes for the textareas in edit mode #description,#results
.tdTextArea {
  max-width: 220px;
}

.textAreaSpan {
  line-break: anywhere;
}

/*input error messages*/

.studentTableErrorMessages {
  color: red;
  font-size: 12px;
}
</style>
